import type { PropertyValues } from "@/wc/registry/lib/framework/lit/index.js";

import {
    LitElement,
    html,
    staticHtml,
    customElement,
    property,
    query,
    map,
    state,
    nothing,
    unsafeStatic,
    classMap,
} from "@/wc/registry/lib/framework/lit/index.js";
import moment from "@/wc/registry/lib/time/moment/index.js";
import { RepositoryDoesNotSupportActionError } from "@/wc/registry/lib/service/repository/base.js";
import { ActionsManager } from "@/wc/registry/lib/service/repository/manager/actions.js";
import type {
    IFields,
    IRepositoryParameters,
    IStekzFieldProperties,
    IStekzSchema,
} from "@/wc/registry/lib/service/repository/types";
import * as config from "@/config";
import serviceRepository from "@/wc/registry/lib/service/repository/index.js";
import format from "@/wc/registry/lib/format/index.js";
import { getFieldProperties } from "@/wc/registry/lib/schema/index.js";

import * as route from "@/wc/registry/lib/route/index.js";
import * as dom from "@/wc/registry/lib/dom/index.js";
import { getFormDataFromElement } from "@/wc/registry/lib/form/index.js";

import {
    throttle,
    has as lodashHas,
    set as lodashSet,
    get as lodashGet,
} from "lodash";
import { monitoring } from "@/lib/integrations/index.js";
import "@/wc/registry/component/form/index.js";
import "@/wc/registry/component/form-input/index.js";

import styles from "@/wc/registry/component/table/styles.js";

// The table columns should be configured through the following format. Field
// means which field belongs to a specific column, where header is used to
// describe which title should be used for that header. Can't rename it yet,
// to keep compatability with the older table.
interface IColumn {
    field: string;
    header: string;
    width?: string;
    type?: "boolean" | "date-time" | "currency";
    badge?: string;
    sortable?: boolean;
    template?: string;
    maxCharacters?: string;
    format?: string;
    currencyCode?: string;
    valueSuffix?: string;
}

interface IRetrieveAndStoreTableDataParams {
    showLoader?: boolean;
    forceRefresh?: boolean;
}

// The 'raw' fields received from the repository - pre-filtering and
// formatting.
type Rows = IFields[];
type FormattedRow = {
    // Keep a reference to the original object to make it easy to extract data
    // from the object to pass along to nested action buttons (e.g. an object
    // id so the deletion button can easily show a deletion modal for the
    // specific object)
    ref: IFields;
    row: (string | number)[];
};
type FormattedRows = FormattedRow[];

export interface IObjectValue {
    value: string;
    badge?: string;
    valueSuffix?: string;
}

// Determine how to display specific data formats in the table.
function formatRows(
    rows: Rows,
    namesOfToBeShownFields: string[],
    repositoryProperties: IStekzSchema | IStekzFieldProperties
): FormattedRows {
    return rows.map((row: IFields) => {
        const formattedRow = namesOfToBeShownFields.map((fieldName) =>
            format(fieldName, row, repositoryProperties)
        );
        return {
            row: formattedRow,
            ref: row,
        };
    });
}

function getPageFromQueryParams() {
    const url = new URL(window.location.href);
    const page = url.searchParams.get("page");
    if (page && !isNaN(Number(page))) {
        return Number(page);
    }

    return 1;
}

function getSearchFromQueryParams() {
    const url = new URL(window.location.href);
    return url.searchParams.get("search") || "";
}

function getSortingOnFromQueryParams() {
    const url = new URL(window.location.href);
    const sortOn = url.searchParams.get("sort-on");
    if (!sortOn) {
        return undefined;
    }
    return sortOn.substring(1);
}

function getSortingAscendingQueryParams(): boolean {
    const url = new URL(window.location.href);
    const sortOn = url.searchParams.get("sort-on");
    if (!sortOn) {
        return undefined;
    }

    return sortOn[0] === "^";
}

// Simply copy-pasted the svgs from bootstrap, as we don't have a library yet
// with shared bootstrap icons. @TODO: Create a bootstrap library for web
// component consumption - #165
// https://icons.getbootstrap.com/icons/chevron-double-left/
// https://icons.getbootstrap.com/icons/chevron-left/
// https://icons.getbootstrap.com/icons/chevron-right/
// https://icons.getbootstrap.com/icons/chevron-double-right/
const chevronDoubleLeft = html`<svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    fill="currentColor"
    class="bi bi-chevron-double-left"
    viewBox="0 0 16 16"
>
    <path
        fill-rule="evenodd"
        d="M8.354 1.646a.5.5 0 0 1 0 .708L2.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z"
    />
    <path
        fill-rule="evenodd"
        d="M12.354 1.646a.5.5 0 0 1 0 .708L6.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z"
    />
</svg>`;
const chevronLeft = html`<svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    fill="currentColor"
    class="bi bi-chevron-left"
    viewBox="0 0 16 16"
>
    <path
        fill-rule="evenodd"
        d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z"
    />
</svg>`;
const chevronRight = html`<svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    fill="currentColor"
    class="bi bi-chevron-right"
    viewBox="0 0 16 16"
>
    <path
        fill-rule="evenodd"
        d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"
    />
</svg>`;
const chevronDown = html`<svg
    xmlns="http://www.w3.org/2000/svg"
    transform="rotate(90)"
    width="16"
    height="16"
    fill="currentColor"
    class="bi bi-chevron-right"
    viewBox="0 0 16 16"
>
    <path
        fill-rule="evenodd"
        d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"
    />
</svg>`;
const chevronDoubleRight = html`<svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    fill="currentColor"
    class="bi bi-chevron-double-right"
    viewBox="0 0 16 16"
>
    <path
        fill-rule="evenodd"
        d="M3.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L9.293 8 3.646 2.354a.5.5 0 0 1 0-.708z"
    />
    <path
        fill-rule="evenodd"
        d="M7.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L13.293 8 7.646 2.354a.5.5 0 0 1 0-.708z"
    />
</svg>`;
const sortDown = html`<svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    fill="currentColor"
    class="bi bi-sort-down"
    viewBox="0 0 16 16"
>
    <path
        d="M3.5 2.5a.5.5 0 0 0-1 0v8.793l-1.146-1.147a.5.5 0 0 0-.708.708l2 1.999.007.007a.497.497 0 0 0 .7-.006l2-2a.5.5 0 0 0-.707-.708L3.5 11.293V2.5zm3.5 1a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7a.5.5 0 0 1-.5-.5zM7.5 6a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1h-5zm0 3a.5.5 0 0 0 0 1h3a.5.5 0 0 0 0-1h-3zm0 3a.5.5 0 0 0 0 1h1a.5.5 0 0 0 0-1h-1z"
    />
</svg>`;
const sortUp = html`<svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    fill="currentColor"
    class="bi bi-sort-up"
    viewBox="0 0 16 16"
>
    <path
        d="M3.5 12.5a.5.5 0 0 1-1 0V3.707L1.354 4.854a.5.5 0 1 1-.708-.708l2-1.999.007-.007a.498.498 0 0 1 .7.006l2 2a.5.5 0 1 1-.707.708L3.5 3.707V12.5zm3.5-9a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7a.5.5 0 0 1-.5-.5zM7.5 6a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1h-5zm0 3a.5.5 0 0 0 0 1h3a.5.5 0 0 0 0-1h-3zm0 3a.5.5 0 0 0 0 1h1a.5.5 0 0 0 0-1h-1z"
    />
</svg>`;
const pen = html`
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 512 512"
        width="16"
        height="16"
        fill="currentColor"
    >
        <path
            d="M362.7 19.3L314.3 67.7 444.3 197.7l48.4-48.4c25-25 25-65.5 0-90.5L453.3 19.3c-25-25-65.5-25-90.5 0zm-71 71L58.6 323.5c-10.4 10.4-18 23.3-22.2 37.4L1 481.2C-1.5 489.7 .8 498.8 7 505s15.3 8.5 23.7 6.1l120.3-35.4c14.1-4.2 27-11.8 37.4-22.2L421.7 220.3 291.7 90.3z"
        />
    </svg>
`;
const smallCancel = html`<svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    fill="currentColor"
    class="bi bi-x"
    viewBox="0 0 16 16"
>
    <path
        d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"
    />
</svg>`;
const check = html`
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 448 512"
        width="16"
        height="16"
        fill="currentColor"
    >
        <path
            d="M438.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L160 338.7 393.4 105.4c12.5-12.5 32.8-12.5 45.3 0z"
        />
    </svg>
`;
const cancel = html`
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 384 512"
        width="16"
        height="16"
        fill="currentColor"
    >
        <path
            d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3 297.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256 342.6 150.6z"
        />
    </svg>
`;
const plus = html`<svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    fill="currentColor"
    class="bi bi-plus"
    viewBox="0 0 16 16"
>
    <path
        d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"
    />
</svg>`;
const remove = html`<svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    fill="currentColor"
    class="bi bi-trash3"
    viewBox="0 0 16 16"
>
    <path
        d="M6.5 1h3a.5.5 0 0 1 .5.5v1H6v-1a.5.5 0 0 1 .5-.5ZM11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3A1.5 1.5 0 0 0 5 1.5v1H2.506a.58.58 0 0 0-.01 0H1.5a.5.5 0 0 0 0 1h.538l.853 10.66A2 2 0 0 0 4.885 16h6.23a2 2 0 0 0 1.994-1.84l.853-10.66h.538a.5.5 0 0 0 0-1h-.995a.59.59 0 0 0-.01 0H11Zm1.958 1-.846 10.58a1 1 0 0 1-.997.92h-6.23a1 1 0 0 1-.997-.92L3.042 3.5h9.916Zm-7.487 1a.5.5 0 0 1 .528.47l.5 8.5a.5.5 0 0 1-.998.06L5 5.03a.5.5 0 0 1 .47-.53Zm5.058 0a.5.5 0 0 1 .47.53l-.5 8.5a.5.5 0 1 1-.998-.06l.5-8.5a.5.5 0 0 1 .528-.47ZM8 4.5a.5.5 0 0 1 .5.5v8.5a.5.5 0 0 1-1 0V5a.5.5 0 0 1 .5-.5Z"
    />
</svg>`;

// When used in 'wrapped mode', the table will be wrapped in a c-form that is
// responsible for supplying the table with data. Data manipulation however,
// is still the tables responsibility.
const wrappedMode = "wrapped";
// When used in 'standalone mode', the table will be responsible for retrieving
// the data itself.
const standaloneMode = "standalone";

// Most entities are exposed with a given 'id' or 'identity' property, which
// we assume is used to identify the row.
function getRowIdentifier(row: FormattedRow) {
    return ["id", "identity"].find(
        (identifier) =>
            identifier in row.ref && row.ref[identifier] !== undefined
    );
}

@customElement("c-table")
export class TableComponent extends LitElement {
    // The big list of fonts is what the old table still uses - so also using
    // that here to keep the styling similar where possible.
    static styles = styles;
    throttledSearch = throttle(this.search, 1000);

    mappedValueConverter = {
        "boolean": (value) => {
            if (value) {
                return check;
            }
            if (this.avoidIconsForFalseValues) {
                return "";
            }
            return cancel;
        },
        "date-time": (value) => {
            return moment.utc(value).local().format("HH:mm:ss, MMM DD YYYY");
        },
        "value-suffix": (value, ref, extraProperties) => {
            if (!extraProperties) {
                return { value };
            }
            const valueSuffix = extraProperties.valueSuffix?.value || null;
            const valueObject: IObjectValue = { value };
            if (valueSuffix) {
                valueObject.valueSuffix = lodashGet(ref, valueSuffix);
            }
            return valueObject;
        },
        "expiry-date": (value) => {
            const dDiff = moment().diff(value);
            const isExpired = dDiff > 0 ? true : false;
            const tableValue: IObjectValue = { value };
            if (isExpired) {
                tableValue.badge = "expired";
            }
            return tableValue;
        },
        "currency": (value, ref, extraProperties) => {
            let currency = undefined;
            if (!extraProperties) {
                return value;
            }
            if ("currencyCode" in extraProperties.currency) {
                currency = extraProperties.currency.currencyCode;
            } else if ("currencyCodeLocator" in extraProperties.currency) {
                currency = lodashGet(
                    ref,
                    extraProperties.currency.currencyCodeLocator
                );
            }

            if (!currency) {
                return value;
            }

            const formatter = new Intl.NumberFormat("nl-NL", {
                style: "currency",
                currency,
                currencyDisplay: "narrowSymbol",
            });
            return formatter.format(value);
        },
        "truncated-text": (value, ref, extraProperties) => {
            if (!extraProperties) {
                return value;
            }
            const maxCharacters = extraProperties.truncatedText?.maxCharacters;
            if (value && maxCharacters && value.length > maxCharacters) {
                const valueObject = { fullValue: value };
                valueObject["truncatedText"] =
                    value.slice(0, maxCharacters) + "...";
                return valueObject;
            }
            return value;
        },
        "default": (value) => {
            if (!value) {
                return undefined;
            }
            return value;
        },
    };

    mappedValueWrapperTemplate = {
        "inline-badge": this.inlineBadgeTemplate,
        "value-suffix": this.valueSuffixTemplate,
        "badge": this.badgeTemplate,
        "image-preview": this.imagePreviewTemplate,
        "right-alignment": this.rightAlignmentTemplate,
        "default": this.defaultTemplate,
        "ellipsis": this.ellipsisTemplate,
        "truncated-text": this.truncatedTextTemplate,
    };

    actionsManager?: ActionsManager;

    @query("c-form")
    form: HTMLElementTagNameMap["c-form"];

    @property({ attribute: "data-service-repository" })
    private _repositoryName: string;

    get repositoryName(): string {
        // When no repository name is given, we still need a repository name to
        // be able to retrieve a repository to be able to render the right types
        // per column etc, so it is likely that the c-table is used in the context
        // of a parent c-form, which has its own repository name configured, which
        // we can borrow to be able to determine the right types.
        if (!this._repositoryName) {
            const parentForm = this.getParentForm();
            if (!parentForm) {
                return "";
            }

            return parentForm.dataset.serviceRepository;
        }

        return this._repositoryName;
    }

    getParentForm(): HTMLElementTagNameMap["c-form"] {
        const parentForm = dom.getFirstMatchingParentElement(
            this,
            "c-form"
        ) as HTMLElementTagNameMap["c-form"];
        if (!parentForm) {
            return;
        }
        return parentForm;
    }

    // When data-name is configured, it will expect a wrapping component to
    // provide the data. This wrapping component will be responsible for
    // retrieving the data and passing it to this component. (typically c-form)
    // The presence of this attribute also makes that the table will function
    // in 'wrapped' mode.
    @property({ attribute: "data-name" })
    name: string;

    @property({ attribute: "data-table-name" })
    tableName: string;

    @property({
        attribute: "data-service-repository-parameters",
        type: Object,
        converter: {
            fromAttribute: (value: string) => {
                return JSON.parse(value);
            },
        },
    })
    serviceRepositoryParameters: IRepositoryParameters;

    // These are 'extra fields' that the table should pass along when doing
    // request to repositories
    @property({
        attribute: "data-service-repository-fields",
        converter: {
            fromAttribute: (value: string) => {
                return JSON.parse(value);
            },
        },
    })
    serviceRepositoryFields: IFields;

    @property({
        attribute: "data-component-for-actions",
        type: Object,
    })
    componentForActions: {
        [actionName: string]: string;
    } = {};

    @property({
        attribute: "data-value",
        type: Array,
    })
    _value: Rows = [];

    @property()
    get value() {
        // Currently this getter should only be used in "wrapped" mode, for
        // example by a c-form to retrieve the value of the table.
        if (this.mode === "standalone") {
            return null;
        }

        // When there is no differentiation between how data needs to be send
        // to the backend and how it needs to be displayed, we can assume that
        // the data is already formatted correctly.
        if (!this.dataset.displayColumns) {
            return JSON.parse(JSON.stringify(this._value));
        }

        return this.getDataInDefaultFormat(
            JSON.parse(JSON.stringify(this._value))
        );
    }

    // The 'default format' meaning the format that is used to send data to the
    // backend.
    getDataInDefaultFormat(rows: Rows) {
        if (!this.dataset.displayColumns) {
            return rows;
        }

        const dataToReturn = [...rows];

        const displayColumns = JSON.parse(this.dataset.displayColumns).map(
            (column: IColumn) => column.field
        );
        const columns = JSON.parse(this.dataset.columns).map(
            (column: IColumn) => column.field
        );

        columns.forEach((fieldName) => {
            const fieldExistsInDisplayColumns = displayColumns.find(
                (column: IColumn) => column === fieldName
            );
            if (fieldExistsInDisplayColumns) {
                return;
            }

            // If a fieldname ends with _id, and it doesn't exist as a name in the
            // display columns, it is likely that we can still retrieve the value,
            // just using a slightly different locator. E.g. the data might contain
            // user: { id: 1 }, where the fieldname is actually called user_id. In
            // this case we make an assumption and try to retrieve the value from
            // user.id instead.
            if (fieldName.endsWith("_id")) {
                const alternateLocator = fieldName.replace("_id", ".id");
                dataToReturn.forEach((row) => {
                    const value = lodashGet(row, alternateLocator);
                    if (value) {
                        lodashSet(row, fieldName, value);
                        delete row[alternateLocator.replace(".id", "")];
                    }
                });
            }
        });

        return dataToReturn;
    }

    // When manipulating a row, certain fields could have a mismatch with the
    // fields that need to be displayed after manipulating a row. This function
    // applies some tricks to get rid of that mismatch, so that after saving e.g.
    // a choicefield with value 2, the value that the user will see is what is
    // configured on the table for the column that is associated with that value,
    // which could e.g. be a description.
    async convertRowToDisplayFormatIfNecessary(row: IFields) {
        // Nothing to do if the row doesn't need to be converted to display format.
        if (!this.dataset.displayColumns) {
            return row;
        }

        const displayColumns = JSON.parse(this.dataset.displayColumns).map(
            (column: IColumn) => column.field
        );

        const alteredRow = { ...row };
        const promises = Object.entries(row).map(async ([fieldName, value]) => {
            if (displayColumns.includes(fieldName)) {
                return;
            }

            // We can only try to find an alternate locator when the _id field
            // exists.
            if (!fieldName.endsWith("_id")) {
                return;
            }

            const alternateFieldLocator = fieldName.replace("_id", ".id");
            lodashSet(alteredRow, alternateFieldLocator, value);

            // At this point, we have correctly set the 'id' for a nested field, but
            // we might still be missing the value which is used to describe the
            // same column in the displayColumns.
            const sharedRootFieldName = fieldName.replace("_id", "");
            const missingColumnFieldName = displayColumns.find(
                (column: string) => column.startsWith(sharedRootFieldName)
            );

            // There is nothing we can do if we can't find a shared root of the to
            // be displayed column.
            if (!missingColumnFieldName) {
                return;
            }

            // But if there is a shared root, we can try to retrieve some info from
            // the repository that might be configured on the related c-form-input.
            const formInput = this.renderRoot.querySelector(
                `c-form-input[data-name="${this.name}.${fieldName}"][data-type="choice"][data-service-repository]`
            );
            if (formInput) {
                const repositoryName = formInput.dataset.serviceRepository;
                const repository = serviceRepository.get(repositoryName);
                const response = await repository.objects.all();
                const options = await response.json();
                const option = options.find(
                    (option) => `${option.id}` === `${value}`
                );

                // If the option doesn't exist, we can't enrich the currently edited
                // row data to include the data that is required for the display column.
                if (!option) {
                    return;
                }

                // A display column could be described as 'load.reference' where we
                // only need the 'reference' fieldname as that is likely to be the name
                // of the field that is returned by the repository.
                const missingNestedFieldName = missingColumnFieldName.replace(
                    `${sharedRootFieldName}.`,
                    ""
                );
                const missingColumnFieldValue = lodashGet(
                    option,
                    missingNestedFieldName
                );
                if (!missingColumnFieldValue) {
                    return;
                }

                lodashSet(
                    alteredRow,
                    missingColumnFieldName,
                    missingColumnFieldValue
                );
            }

            delete alteredRow[fieldName];
        });

        await Promise.all(promises);

        return alteredRow;
    }

    @property()
    formattedRows: FormattedRows = [];

    @property({ attribute: "data-row-count", type: Number })
    rowsPerPage = lodashGet(config, "component.c-table.data-row-count") || 30;

    @property({ attribute: "data-avoid-icons-for-false-values", type: Boolean })
    avoidIconsForFalseValues =
        lodashGet(
            config,
            "component.c-table.data-avoid-icons-for-false-values"
        ) || false;

    @property({ attribute: "data-show-global-filter" })
    showFilter = true;

    // Use a converter so that compact mode can easily be used by configuring
    // as follows:
    // ```
    //  <c-table data-compact></c-table>
    // ```
    //
    // Instead of
    // ```
    //  <c-table data-compact="true"></c-table>
    // ```
    @property({
        attribute: "data-compact",
        converter: {
            fromAttribute: (value: string) => {
                if (value === "false") {
                    return false;
                }

                return true;
            },
        },
    })
    compact = false;

    @property({
        attribute: "data-inline-create",
        converter: {
            fromAttribute: (value: string) => {
                if (value === "false") {
                    return false;
                }

                return true;
            },
        },
    })
    inlineCreate = false;

    @property({
        attribute: "data-inline-edit",
        converter: {
            fromAttribute: (value: string) => {
                if (value === "false") {
                    return false;
                }

                return true;
            },
        },
    })
    inlineEdit = false;

    @property({
        attribute: "data-inline-delete",
        converter: {
            fromAttribute: (value: string) => {
                if (value === "false") {
                    return false;
                }

                return true;
            },
        },
    })
    inlineDelete = false;

    // The buttons that we should show per row. These action buttons are other
    // web components that will be fetched and rendered for each row.
    @property({
        attribute: "data-action-items",
        converter: {
            fromAttribute: (value: string) =>
                value.split(",").filter((name) => name !== ""),
        },
    })
    actionButtons = [];

    @property({
        attribute: "data-action-row-item-properties",
        converter: {
            fromAttribute: (value: string) =>
                value
                    .replaceAll(" ", "")
                    .split(",")
                    .filter((name) => name !== ""),
        },
    })
    actionButtonsProperties = [];

    @property({
        attribute: "data-scrollable",
        converter: {
            fromAttribute: (value: string) => {
                if (value === "false") {
                    return false;
                }

                return true;
            },
        },
    })
    scrollable = false;

    @property({
        attribute: "data-avoid-search",
        converter: {
            fromAttribute: (value: string) => {
                if (value === "false") {
                    return false;
                }

                return true;
            },
        },
    })
    isAvoidSearch = false;

    @property({
        attribute: "data-avoid-toast-on-submit",
        converter: {
            fromAttribute: (value: string) => {
                if (value === "false") {
                    return false;
                }

                return true;
            },
        },
    })
    avoidToastOnSubmit = false;

    // Allow opting out of action generation through the application
    // configuration.
    @property({
        attribute: "data-avoid-generating-actions",
        converter: {
            fromAttribute: (value: string) => {
                if (value === "false") {
                    return false;
                }

                return true;
            },
        },
    })
    avoidGeneratingActions =
        lodashGet(config, "component.c-table.data-avoid-generating-actions") ||
        false;

    @property({ type: String, attribute: "data-component-on-expand" })
    componentOnExpand = undefined;

    @state()
    loading = true;

    @state()
    expandedRow: number;

    @state()
    sortingOn = getSortingOnFromQueryParams();

    @state()
    sortAscending = getSortingAscendingQueryParams();

    @state()
    searchFor = getSearchFromQueryParams();

    @state()
    currentPage = getPageFromQueryParams();

    @state()
    isPaginatedThroughRepository = false;

    // Note, no need for state as it isn't tied to Lit's rendering cycle
    isInitialLoadCompleted = false;

    // Used to determine what request has taken place before
    requestHash: string;

    // Below is only used when paginated through repository
    @state()
    totalPages: number;
    @state()
    total: number;
    @state()
    isSearchableThroughRepository = false;
    @state()
    isSortableThroughRepository = false;
    // Above is only used when paginated through repository

    @state()
    hasItemActionsAccordingToRepository = false;

    @state()
    maxPageButtons = 5;

    @state()
    rowNumberBeingEdited?: number;

    @state()
    rowNumberBeingDeleted?: number;

    @state()
    rowData = undefined;

    @state()
    isCreating = false;

    @state()
    deleteConfirmationTimer: ReturnType<typeof setInterval>;

    @state()
    badges: string[] = [];

    @state()
    timeLeft: number;

    @state()
    columns: IColumn[];

    @state()
    repositoryProperties: IStekzSchema | IStekzFieldProperties;

    @state()
    tableBadgeObject = null;

    @state()
    converterPerColumn = [];

    @state()
    templatePerColumn = [];

    @state()
    extraPropertiesPerColumn = [];

    get mode(): typeof standaloneMode | typeof wrappedMode {
        // Wrapped meaning, the table is wrapped by another component that is
        // responsible for retrieving the data.
        if (this.name) {
            return wrappedMode;
        }

        // Standalone meaning, the table is responsible for retrieving its own
        // data.
        if (this.repositoryName) {
            return standaloneMode;
        }

        return undefined;
    }

    @property({ attribute: "data-columns" })
    _columns;

    setColumns() {
        // 'displayColumns' property allows you to optionally show different
        // columns when you are not actively creating or editing a row
        const showDisplayColumns =
            "displayColumns" in this.dataset &&
            this.rowNumberBeingEdited === undefined &&
            !this.isCreating;

        let columns: IColumn[];
        if (showDisplayColumns) {
            columns = JSON.parse(this.dataset.displayColumns);
        } else {
            columns = JSON.parse(this.dataset.columns);
        }

        this.columns = columns.map((column) => {
            // If not sortable is not explicitly defined, then default to true.
            if (!("sortable" in column)) {
                column["sortable"] = true;
            }
            return column;
        });
    }

    get columnHeaders(): string[] {
        return this.columns.map(({ header }) => header);
    }

    get columnNames(): string[] {
        return this.columns.map(({ field }) => field);
    }

    get colspan(): number {
        let numberOfColumns = this.columnNames.length;

        if (this.needActionColumn()) {
            numberOfColumns += 1;
        }

        // When componentOnExpand is set, there needs to be an extra columns to
        // show the 'expand row' button.
        if (this.componentOnExpand) {
            numberOfColumns += 1;
        }

        return numberOfColumns;
    }

    handleEvent(event) {
        switch (event.type) {
            case "created":
            case "updated":
            case "deleted":
                this.retrieveAndStoreTableData({ forceRefresh: true });
                break;
            case "navigate": {
                const currentPage = getPageFromQueryParams();
                const searchFor = getSearchFromQueryParams();
                const sortingOn = getSortingOnFromQueryParams();
                const sortAscending = getSortingAscendingQueryParams();

                const areValuesUnchanged =
                    currentPage === this.currentPage &&
                    searchFor === this.searchFor &&
                    sortingOn === this.sortingOn &&
                    sortAscending === this.sortAscending;
                if (areValuesUnchanged) {
                    return;
                }

                this.searchFor = searchFor;
                this.currentPage = currentPage;
                this.sortingOn = sortingOn;
                this.sortAscending = sortAscending;
                if (this.isPaginatedThroughRepository) {
                    this.retrieveAndStoreTableData({ forceRefresh: true });
                }
            }
        }
    }

    // this is a helper function to query select the elements within the table
    // that are not rendered in the light dom
    async asyncQuerySelector(selector) {
        await this.updateComplete;
        return this.renderRoot?.querySelector(selector);
    }

    setRepositoryRelatedProperties() {
        const repository = serviceRepository.get(this.repositoryName);
        this.isPaginatedThroughRepository =
            !!repository?.actionPaths?.all?.info?.isPaginated;
        this.isSearchableThroughRepository =
            !!repository?.actionPaths?.all?.info?.isSearchable;
        this.isSortableThroughRepository =
            !!repository?.actionPaths?.all?.info?.isSortable;
        this.hasItemActionsAccordingToRepository =
            !!repository?.actionPaths?.all?.info?.hasItemActions;
    }

    willUpdate(changedProperties: PropertyValues<this>) {
        if (changedProperties.has("repositoryName")) {
            this.setRepositoryRelatedProperties();
        }

        if (
            [
                "repositoryName",
                "serviceRepositoryParameters",
                "serviceRepositoryFields",
                "_value",
            ].some((propertyName) => changedProperties.has(propertyName))
        ) {
            this.retrieveAndStoreTableData({ showLoader: true });
        }

        // When the columns change, we need to reconfigure the converters and
        // templates per column and rerender the table with the new columns.
        if (changedProperties.has("_columns")) {
            this.setColumns();
            this.configureConvertersAndTemplatesPerColumn();
            this.retrieveAndStoreTableData();
        }
    }

    // Just an alias to be used by external components.
    async refresh() {
        await this.retrieveAndStoreTableData({ forceRefresh: true });
    }

    async retrieveAndStoreTableData(
        params?: IRetrieveAndStoreTableDataParams
    ): Promise<void> {
        const { showLoader = false, forceRefresh = false } = params || {};
        if (showLoader) {
            this.loading = true;
        }

        const newHash = this.getRequestHash();
        if (newHash === this.requestHash && !forceRefresh) {
            this.loading = false;
            return;
        }

        let rows = this._value;
        if (this.mode === standaloneMode) {
            this.requestHash = newHash;
            rows = this.isPaginatedThroughRepository
                ? await this.getPaginatedTableData()
                : await this.getTableData();
        }

        // When rows is undefined, likely the request has failed. When this
        // happens, just put an empty array to avoid errors in the table rendering
        // process.
        if (rows === undefined) {
            rows = [];
        }

        if (this.shouldGenerateActions()) {
            await this.actionsManager.collectActionsForItems(rows);
        }

        this.formattedRows = formatRows(
            rows,
            this.columnNames,
            this.repositoryProperties
        );

        this.badges = this.extractBadgesFromColumns();
        this.loading = false;
    }

    // The request hash can be used to determine what signature the next request
    // would use. This is useful to determine if the request has already been
    // made.
    getRequestHash() {
        return `${this.repositoryName}-${JSON.stringify(
            this.getRepositoryParameters()
        )}${JSON.stringify(this.serviceRepositoryFields)}`;
    }

    async getTableData() {
        if (this.mode === wrappedMode) {
            return this._value;
        }

        const repository = serviceRepository.get(this.repositoryName);
        const response = await repository.objects.all(
            this.getRepositoryParameters(),
            this.serviceRepositoryFields,
            { renewCacheEntry: true }
        );

        return response.json();
    }

    getRepositoryParameters() {
        if (!this.isPaginatedThroughRepository) {
            return this.serviceRepositoryParameters;
        }

        const params = {
            ...this.serviceRepositoryParameters,
            size: this.rowsPerPage,
            page: this.currentPage,
        };

        if (this.isSearchableThroughRepository && this.searchFor.length > 0) {
            params["search"] = this.searchFor;
        }

        if (this.isSortableThroughRepository && this.sortingOn) {
            const column = this.columns.find(
                ({ field, sortable }) => field === this.sortingOn && sortable
            );
            if (column) {
                // The backend uses "__" to describe related fields, which is why we
                // need to replace "." with "__".
                const field = column.field.replace(".", "__");
                const prefix = this.sortAscending ? "+" : "-";
                params["sort_by"] = [`${prefix}${field}`];
            }
        }
        return params;
    }

    async getPaginatedTableData() {
        const repository = serviceRepository.get(this.repositoryName);

        const response = await repository.objects.all(
            this.getRepositoryParameters(),
            this.serviceRepositoryFields,
            { renewCacheEntry: true }
        );
        const data = await response.json();

        this.totalPages = data["total_pages"];
        this.total = data.total;

        return data.items;
    }

    extractBadgesFromColumns() {
        let badges = [];
        this.columns.forEach((column) => {
            if ("badge" in column) {
                this.tableBadgeObject = lodashGet(
                    config,
                    `badgeStyles.${column.badge}`
                );
                const batchKeys = Object.keys(this.tableBadgeObject);
                badges = badges.concat(batchKeys);
            }
        });

        return badges;
    }

    isConfigured(): boolean {
        if (!this.dataset.columns) {
            throw Error(
                `'${this.tagName}' requires the 'data-columns' attribute to be configured.`
            );
        }

        if (this.mode === undefined) {
            throw Error(
                `'${this.tagName}' requires either the 'data-service-repository' attribute to be configured so it can function on its own, or the 'data-name' attribute to be configured if you want to it to be controlled by another component.`
            );
        }

        if (this.mode === standaloneMode) {
            const repository = serviceRepository.get(this.repositoryName);
            try {
                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                repository.properties.all;
            } catch (e) {
                if (e instanceof RepositoryDoesNotSupportActionError) {
                    monitoring.error(
                        `Cannot show row data through the ${this.tagName.toLowerCase()} component - there is no 'all' action defined in the '${
                            this.repositoryName
                        }' repository.`
                    );
                } else {
                    throw e;
                }

                return false;
            }

            if (this.inlineCreate) {
                try {
                    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                    repository.properties.create;
                } catch (e) {
                    if (e instanceof RepositoryDoesNotSupportActionError) {
                        monitoring.error(
                            `Cannot use inline-create through the ${this.tagName.toLowerCase()} component - there is no 'create' action defined in the '${
                                this.repositoryName
                            }' repository, so there is no way to create rows.`
                        );
                    } else {
                        throw e;
                    }

                    return false;
                }
            }

            if (this.inlineEdit) {
                try {
                    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                    repository.properties.get;
                } catch (e) {
                    if (e instanceof RepositoryDoesNotSupportActionError) {
                        monitoring.error(
                            `Cannot use inline-edit through the ${this.tagName.toLowerCase()} component - there is no 'get' action defined in the '${
                                this.repositoryName
                            }' repository, so there is no way to fill row forms.`
                        );
                    } else {
                        throw e;
                    }

                    return false;
                }

                try {
                    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                    repository.properties.update;
                } catch (e) {
                    if (e instanceof RepositoryDoesNotSupportActionError) {
                        monitoring.error(
                            `Cannot use inline-edit through the ${this.tagName.toLowerCase()} component - there is no 'update' action defined in the '${
                                this.repositoryName
                            }' repository, so there is no way to update rows.`
                        );
                    } else {
                        throw e;
                    }

                    return false;
                }
            }

            if (this.inlineDelete && !("delete" in repository.actionPaths)) {
                monitoring.error(
                    `Cannot use inline-delete through the ${this.tagName.toLowerCase()} component - there is no 'delete' action defined in the '${
                        this.repositoryName
                    }' repository, so there is no way to delete rows.`
                );
                return false;
            }
        }

        return true;
    }

    // The table doesn't support mapping types from external repositories just
    // yet, so if fieldProperties is undefined, we'll just use the type
    // configured on the column itself or fall back to the default.
    getTypeOrFormatForColumn(
        column: IColumn,
        fieldProperties: IStekzFieldProperties | IStekzSchema
    ): string {
        let typeOrFormat = undefined;
        if ("format" in column) {
            typeOrFormat = column.format;
        } else if ("type" in column) {
            typeOrFormat = column.type;
        } else if (fieldProperties) {
            typeOrFormat = fieldProperties.type;

            if (fieldProperties.format) {
                typeOrFormat = fieldProperties.format;
            }
        }

        if ("valueSuffix" in column) {
            typeOrFormat = "value-suffix";
        }

        return typeOrFormat;
    }

    getProperties() {
        const repository = serviceRepository.get(this.repositoryName);

        let action = "all";
        if (this.mode === wrappedMode) {
            const parentForm = this.getParentForm();
            if (parentForm) {
                action = parentForm.dataset.action;
            }
        }

        let properties: IStekzFieldProperties | IStekzSchema =
            repository.properties[action];

        // When the table is used in the context of a form, instead of getting
        // the root values of the related repository, get the properties of the
        // nested field.
        if (this.mode === wrappedMode) {
            // When nested, we should navigate to properties using
            // fieldName.properties.nestedFieldName instead of fieldName.nestedFieldName.
            const fieldLocator = this.name.split(".").join(".properties.");
            // Add another .properties, since otherwise the 'object' type would be
            // described instead of the fields in the object.
            properties = lodashGet(properties, `${fieldLocator}.properties`);
        } else {
            // When the table is paginated (which is only the case when the table is
            // responsible for retrieving its own values), the stekz schema will
            // contain a field for 'items', in which the properties for the objects
            // in the array 'items' are described.
            if (
                this.isPaginatedThroughRepository &&
                repository.properties[action].items?.properties
            ) {
                properties = repository.properties[action].items.properties;
            }
        }

        return properties;
    }

    configureConvertersAndTemplatesPerColumn() {
        this.repositoryProperties = this.getProperties();
        this.columns.forEach((column, index) => {
            const fieldProperties = getFieldProperties(
                column.field,
                this.repositoryProperties
            );
            const typeOrFormat = this.getTypeOrFormatForColumn(
                column,
                fieldProperties
            );

            // set the value converter based on the type or format
            if (typeOrFormat in this.mappedValueConverter) {
                if (typeOrFormat === "value-suffix") {
                    this.extraPropertiesPerColumn[index] = {
                        valueSuffix: {
                            value: column.valueSuffix,
                        },
                    };
                } else if (typeOrFormat === "currency") {
                    if ("currencyCode" in column) {
                        this.extraPropertiesPerColumn[index] = {
                            currency: {
                                currencyCode: column.currencyCode,
                            },
                        };
                    } else if (
                        fieldProperties &&
                        "currencyCodeLocator" in fieldProperties
                    ) {
                        this.extraPropertiesPerColumn[index] = {
                            currency: {
                                currencyCodeLocator:
                                    fieldProperties.currencyCodeLocator,
                            },
                        };
                    }
                } else if (
                    typeOrFormat === "truncated-text" &&
                    "maxCharacters" in column
                ) {
                    this.extraPropertiesPerColumn[index] = {
                        truncatedText: {
                            maxCharacters: column.maxCharacters,
                        },
                    };
                }
                this.converterPerColumn[index] = typeOrFormat;
            } else {
                this.converterPerColumn[index] = "default";
            }

            // set the template of the table cell based on the type or format
            if (typeOrFormat === "currency") {
                this.templatePerColumn[index] =
                    this.mappedValueWrapperTemplate["right-alignment"];
            } else if ("valueSuffix" in column) {
                this.templatePerColumn[index] =
                    this.mappedValueWrapperTemplate["value-suffix"];
            } else if (typeOrFormat === "truncated-text") {
                if ("maxCharacters" in column) {
                    this.templatePerColumn[index] =
                        this.mappedValueWrapperTemplate["truncated-text"];
                } else {
                    this.templatePerColumn[index] =
                        this.mappedValueWrapperTemplate["ellipsis"];
                }
            } else if (!("template" in column)) {
                this.templatePerColumn[index] =
                    this.mappedValueWrapperTemplate["default"];
            } else {
                this.templatePerColumn[index] =
                    this.mappedValueWrapperTemplate[column.template];
            }
        });
    }

    async connectedCallback() {
        const isConfigured = this.isConfigured();
        if (!isConfigured) {
            return;
        }

        super.connectedCallback();
        this.setColumns();
        this.setRepositoryRelatedProperties();
        this.configureConvertersAndTemplatesPerColumn();

        if (this.shouldGenerateActions()) {
            if (!this.actionsManager) {
                this.actionsManager = new ActionsManager(this.repositoryName);
            }
            this.actionsManager.events.addEventListener("created", this);
            this.actionsManager.events.addEventListener("updated", this);
            this.actionsManager.events.addEventListener("deleted", this);
        }

        await this.retrieveAndStoreTableData();
        if (this.mode === standaloneMode) {
            const repository = serviceRepository.get(this.repositoryName);
            ["created", "updated", "deleted"].forEach((eventType) => {
                repository.events.addEventListener(eventType, this);
            });
        }

        route.routeEvents.addEventListener("navigate", this);
        this.isInitialLoadCompleted = true;
    }

    disconnectedCallback() {
        super.disconnectedCallback();

        if (this.mode === standaloneMode) {
            const repository = serviceRepository.get(this.repositoryName);
            ["created", "updated", "deleted"].forEach((eventType) => {
                repository.events.removeEventListener(eventType, this);
            });
        }

        if (this.shouldGenerateActions()) {
            this.actionsManager.events.removeEventListener("created", this);
            this.actionsManager.events.removeEventListener("updated", this);
            this.actionsManager.events.removeEventListener("deleted", this);
        }
    }

    sort(column: IColumn) {
        if (!column.sortable) {
            return;
        }

        if (this.sortingOn !== column.field) {
            this.sortingOn = column.field;
            this.sortAscending = true;
        } else {
            if (!this.sortAscending) {
                this.sortingOn = undefined;
            }
            this.sortAscending = !this.sortAscending;
        }

        this.currentPage = 1;

        const prefix = this.sortAscending ? "^" : "-";
        if (this.sortingOn) {
            route.updateURLSearchParams({
                "page": this.currentPage,
                "sort-on": `${prefix}${column.field}`,
            });
        } else {
            route.updateURLSearchParams({
                "page": this.currentPage,
                "sort-on": undefined,
            });
        }

        if (this.isPaginatedThroughRepository) {
            this.retrieveAndStoreTableData();
        }
    }

    search(value?: string) {
        this.expandedRow = undefined;
        this.searchFor = value;
        this.currentPage = 1;
        route.updateURLSearchParams(
            { page: `${this.currentPage}` },
            { replace: true }
        );

        if (this.isPaginatedThroughRepository) {
            this.retrieveAndStoreTableData();
        }

        // This one is tricky - but we want to make sure that if a user goes to
        // the next page and navigates to the previous page, that the search filter
        // is still active. However, we can't create new history entries every 100
        // milliseconds every time a new character is entered. Well, we can, but
        // that would mean that the user has to click 'previous' a lot of times
        // before they would end up in the page before they started searching for
        // data in the table. So instead, we check if the value hasn't changed for
        // 1.5 seconds. If so, we store the keyword that is searched for in the
        // url, so that it can be re-used when we re-visit the page through history
        // navigation.
        setTimeout(() => {
            if (value === this.searchFor) {
                route.updateURLSearchParams({
                    search: `${this.searchFor}`,
                    page: `${this.currentPage}`,
                });
            }
        }, 1500);
    }

    sortingIconTemplate(fieldName: string) {
        if (this.sortingOn !== fieldName) {
            return html``;
        }

        if (this.sortAscending) {
            return sortUp;
        }

        return sortDown;
    }

    headersTemplate() {
        return html` <thead
            class=${this.scrollable ? "fixed-header" : ""}
            part="thead"
        >
            <tr>
                ${this.componentOnExpand
                    ? html`<th class="expanded-icon-table-header"></th>`
                    : ``}
                ${map(this.columns, (column) => this.headerTemplate(column))}
                ${this.needActionColumn()
                    ? html`<th class="action-buttons"></th>`
                    : ``}
            </tr>
        </thead>`;
    }

    headerTemplate(column: IColumn) {
        // Avoid using '.' as it is a special character in css to select classes
        const partName = `th-${column.field}`.replaceAll(".", "-");

        if (
            this.isPaginatedThroughRepository &&
            !this.isSortableThroughRepository
        ) {
            // Avoid setting 'width' through element attributes, and set them
            // through 'c-table::part(header-<name>)' instead.
            return html`<th
                style="${column.width ? "width:" + column.width : ""}"
                part="${partName}"
            >
                <span>${column.header}</span>
            </th>`;
        }

        return html`<th
            @click=${() => {
                this.sort(column);
            }}
            class=${column.sortable ? "" : "not-sortable"}
            style="${column.width ? "width:" + column.width : ""}"
            part="${partName}"
        >
            <span
                >${column.header}${this.sortingIconTemplate(column.field)}</span
            >
        </th>`;
    }

    getColumnHeaders() {
        return this.columnHeaders;
    }

    shouldShowPaginator(rows: (string | number)[][]) {
        if (this.loading) {
            return false;
        }

        if (this.isPaginatedThroughRepository) {
            return this.totalPages > 1;
        }

        return rows.length > this.rowsPerPage;
    }

    getSortedAndFilteredRows() {
        // When the rows are paginated through the repository, do not apply any
        // frontend sorting or filtering. These should all go through the backend,
        // in which case the filtered and sorted rows are already present in the
        // formattedRows.
        if (this.isPaginatedThroughRepository) {
            return this.formattedRows;
        }

        let rows = [...this.formattedRows];
        if (this.sortingOn) {
            const columnNumber = this.columnNames.findIndex(
                (headerName) => headerName === this.sortingOn
            );

            const canSortOnNumber = rows.every((rowObj) => {
                const value = Number(rowObj.row[columnNumber]);
                return !isNaN(value) && isFinite(value);
            });

            if (canSortOnNumber) {
                rows = rows.sort((a, b) =>
                    this.sortAscending
                        ? Number(a.row[columnNumber]) -
                          Number(b.row[columnNumber])
                        : Number(b.row[columnNumber]) -
                          Number(a.row[columnNumber])
                );
            } else {
                rows = rows.sort((a, b) =>
                    this.sortAscending
                        ? `${a.row[columnNumber]}`.localeCompare(
                              `${b.row[columnNumber]}`
                          )
                        : `${b.row[columnNumber]}`.localeCompare(
                              `${a.row[columnNumber]}`
                          )
                );
            }
        }

        if (this.searchFor) {
            rows = rows.filter((rowEntries) =>
                rowEntries.row.find((entry) =>
                    `${entry}`
                        .toLowerCase()
                        .includes(this.searchFor.toLowerCase())
                )
            );
        }

        return rows;
    }

    // E.g. when on page 2 and showing 20 rows per page, table should show
    // rows from 20 onwards.
    getStartRowNr(): number {
        return (this.currentPage - 1) * this.rowsPerPage;
    }

    // E.g. when on page 2 and showing 20 rows per page, table should stop
    // showing rows after 40 rows when possible.
    getEndRowNr(rows: FormattedRows, start: number): number {
        // E.g. when there are only 35 rows, only show 35.
        if (start + this.rowsPerPage > rows.length) {
            return rows.length + 1;
        }
        return start + this.rowsPerPage;
    }

    getFakePaginatedRows(rows: FormattedRows) {
        const start = this.getStartRowNr();
        const end = this.getEndRowNr(rows, start);
        return rows.slice(start, end);
    }

    async editRow(row: FormattedRow, rowNumber: number) {
        this.stopCreating();
        this.stopDeleting();
        this.rowNumberBeingEdited = rowNumber;
        this.setColumns();

        if (this.mode === standaloneMode) {
            const repository = serviceRepository.get(this.repositoryName);
            const { parameterName, parameterValue } =
                this.getParameterNameAndValueForRow("get", row);
            const props = {
                ...this.form.getRepositoryParameters(),
                [parameterName]: parameterValue,
            };
            const response = await repository.objects.get(
                props,
                this.serviceRepositoryFields
            );
            this.rowData = await response.json();
        } else if (this.mode === wrappedMode) {
            this.rowData = row.ref;
        }
    }

    async openDeleteRowConfirmation(rowNumber: number) {
        this.stopCreating();
        this.stopEditing();

        this.expandedRow = undefined;
        this.rowNumberBeingDeleted = rowNumber;
    }

    async deleteRow(row: FormattedRow, rowNumber) {
        if (this.mode === standaloneMode) {
            const repository = serviceRepository.get(this.repositoryName);
            const { parameterName, parameterValue } =
                this.getParameterNameAndValueForRow("delete", row);
            const props = {
                ...this.form.getRepositoryParameters(),
                [parameterName]: parameterValue,
            };

            await repository.objects.delete(
                props,
                this.serviceRepositoryFields
            );
        } else if (this.mode === wrappedMode) {
            this._value.splice(rowNumber, 1);
            this.retrieveAndStoreTableData({ forceRefresh: true });
            this.dispatchEvent(
                new CustomEvent("wrapped-mode-delete-clicked", {
                    detail: {
                        row: this._value,
                        target: this,
                    },
                    bubbles: true,
                    composed: true,
                })
            );
        }

        this.onReleasingDeleteConfirmation();
        this.stopDeleting();
    }

    async save(action = "update") {
        let success = false;
        if (this.mode === standaloneMode) {
            const extraFormData = action === "update" ? this.rowData : {};
            // Sometimes the form inputs in the table row dont contain all form data,
            // so add the rest of the existing object so that the form submission won't
            // accidentally empty any existing fields.
            success = await this.form.submitForm(undefined, {
                ...extraFormData,
            });
        } else if (this.mode === wrappedMode) {
            if (!this.renderRoot) {
                return;
            }
            // handle form validation in the frontend since the form is not on standaloneMode
            let hasInputErrors = false;
            this.renderRoot
                .querySelectorAll("c-form-input")
                .forEach((input) => {
                    if (input.dataset.required !== undefined && !input.value) {
                        hasInputErrors = true;
                        input.error = "This field is required";
                    }
                });
            // if there are any form input errors, don't save
            if (hasInputErrors) {
                return;
            }

            const formData = await getFormDataFromElement(this.renderRoot);
            const entry = lodashGet(formData, this.name);

            if (action === "create") {
                this._value.push(
                    await this.convertRowToDisplayFormatIfNecessary(entry)
                );
            } else if (action === "update") {
                const updatedData =
                    await this.convertRowToDisplayFormatIfNecessary(entry);
                Object.assign(
                    this._value[this.rowNumberBeingEdited],
                    updatedData
                );
            }

            this.dispatchEvent(
                new CustomEvent("wrapped-mode-save-clicked", {
                    detail: {
                        row: this._value,
                        target: this,
                    },
                    bubbles: true,
                    composed: true,
                })
            );
            success = true;
        }

        // Can close the row on success - otherwise keep showing possible errors.
        // No need to re-fetch, as that is indirectly done by listening to
        // repository events.
        if (success) {
            this.stopCreating();
            this.stopEditing();
            this.setColumns();

            if (this.mode === wrappedMode) {
                // If we were working in 'standalone' mode, an event would fire from the
                // repository that we called an action on, but in this case there are
                // no events to listen to, and we're managing the data internally, so
                // we can manually retrieve the new rows (from the updated this._value)
                // and prepare the data for display.
                this.retrieveAndStoreTableData();
            }
        }
    }

    stopCreating() {
        this.isCreating = false;
    }
    stopEditing() {
        this.rowNumberBeingEdited = undefined;
    }
    stopDeleting() {
        this.rowNumberBeingDeleted = undefined;
    }

    // When modifying data through repositories for a specific row, it might be
    // that the repository requires extra information in order to do so. This
    // function extracts that information so it can be passed along to the
    // repository when executing the repository action.
    getParameterNameAndValueForRow(
        action: "get" | "update" | "delete",
        row: FormattedRow
    ): {
        parameterName?: string;
        parameterValue?: string | number;
    } {
        const repository = serviceRepository.get(this.repositoryName);
        const requiredParameters =
            repository?.actionPaths?.[action]?.requiredParameters;

        if (!requiredParameters) {
            return {};
        }
        const rowIdentifier = getRowIdentifier(row);
        if (!rowIdentifier) {
            return {};
        }

        if (
            typeof row.ref[rowIdentifier] === "string" ||
            typeof row.ref[rowIdentifier] === "number"
        ) {
            const parameterName =
                requiredParameters[requiredParameters.length - 1];
            const parameterValue = row.ref[rowIdentifier];
            return { parameterName, parameterValue };
        }

        return {};
    }

    inlineEditCancelButtonTemplate() {
        return html` <button
            class="inline-cancel-button
            ${this.compact ? "compact" : ""}"
            @click=${() => {
                this.stopEditing();
                this.setColumns();
            }}
        >
            ${smallCancel}
        </button>`;
    }

    inlineEditButtonTemplate(
        row: FormattedRow,
        rowNumber: number,
        isEditing: boolean,
        isDeleting: boolean
    ) {
        if (isDeleting) {
            return html``;
        }

        if (!this.inlineEdit) {
            return html``;
        }

        // WARNING
        //
        // Making some assumptions here! The following code is only used when the
        // repository is configured to be inline-editable. For a given 'action',
        // in this case the 'update' action, we check out what parameter the
        // repository needs in order to do the update action. We're assuming that
        // the last property required for the given action can be associated with
        // the id of the current row. This will usually work fine with
        // repositories generated from CRUD-like api's, but might not with
        // other types of api's, but in that case an error will be thrown when
        // configuring the specific table.
        //
        // Example:
        //
        // for an openapi generated crud api, we might have the following endpoints:
        //  - /product/             (GET) - repository.objects.all()
        //  - /product/{product_id} (GET) - repository.objects.get()
        //  - /product/{product_id} (PUT) - repository.objects.update()
        //
        //
        // Using the above example, when the table is generated, only the first
        // endpoint is used to retrieve all the rows. However, when clicking the
        // 'inline edit button', the specific row data will be fetched from the
        // second endpoint to prefill the inline form. In this case, the
        // product_id in the example is assumed to be the id that is found in the
        // object for the specific row. Finally, when clicking the 'submit' button
        // the third endpoint is used.
        if (isEditing) {
            const { parameterName, parameterValue } =
                this.getParameterNameAndValueForRow("update", row);
            if (parameterName && parameterValue !== undefined) {
                const parameterNameAsAttribute = parameterName.replaceAll(
                    "_",
                    "-"
                );
                return staticHtml`
          <c-form-button
            data-compact=${this.compact}
            ${unsafeStatic(parameterNameAsAttribute)}=${parameterValue}
            data-proxy-action="submit"
            @click=${() => this.save("update")} data-inline>
          </c-form-button>
          ${this.inlineEditCancelButtonTemplate()}`;
            }

            return html`<c-form-button
                    ?data-compact=${this.compact}
                    data-inline
                    data-proxy-action="submit"
                    @click="${() => this.save("update")}"
                ></c-form-button>
                ${this.inlineEditCancelButtonTemplate()}`;
        }

        return html`<button
            @click=${() => this.editRow(row, rowNumber)}
            class="inline-edit-button ${this.compact ? "compact" : ""}"
        >
            ${pen}
        </button>`;
    }

    onReleasingDeleteConfirmation() {
        clearInterval(this.deleteConfirmationTimer);
        this.deleteConfirmationTimer = undefined;
    }

    onHoldingDeleteConfirmation(event, row: FormattedRow, rowNumber: number) {
        // When pressing a key to confirm, only allow the 'ENTER' key.
        if (event.type === "keydown" && event.keyCode !== 13) {
            return;
        }

        // Only set state the first time the event comes by. When someone is e.g.
        // holding the enter key 'repeat' will be true.
        if (event.repeat) {
            return;
        }

        this.timeLeft = 1100;
        const interval = 100;
        this.deleteConfirmationTimer = setInterval(() => {
            this.timeLeft -= interval;

            if (this.timeLeft <= 0) {
                this.deleteRow(row, rowNumber);
                clearInterval(this.deleteConfirmationTimer);
            }
        }, interval);
    }

    shouldRowHaveDeleteButton(row: FormattedRow) {
        if (this.inlineDelete) {
            return true;
        }

        if (this.shouldGenerateActions()) {
            return this.actionsManager.getAllowedStandardActionsForItem(row.ref)
                .delete;
        }

        return false;
    }

    inlineDeleteButtonTemplate(
        row: FormattedRow,
        rowNumber: number,
        isEditing: boolean,
        isDeleting: boolean
    ) {
        if (!this.shouldRowHaveDeleteButton(row)) {
            return html``;
        }

        if (isEditing) {
            return html``;
        }

        const onHold = (e) =>
            this.onHoldingDeleteConfirmation(e, row, rowNumber);
        if (isDeleting) {
            return html`<button
                    @mouseout=${this.onReleasingDeleteConfirmation}
                    @keyup=${this.onReleasingDeleteConfirmation}
                    @keydown=${onHold}
                    @mouseup=${this.onReleasingDeleteConfirmation}
                    @mousedown=${onHold}
                    class="inline-delete-confirmation-button ${this.compact
                        ? "compact"
                        : ""} ${this.deleteConfirmationTimer
                        ? "is-deleting"
                        : ""}"
                >
                    Hold to delete
                </button>
                <button
                    class="inline-cancel-button ${this.compact
                        ? "compact"
                        : ""}"
                    @click=${() => {
                        this.stopDeleting();
                        this.setColumns();
                    }}
                >
                    ${smallCancel}
                </button> `;
        }

        return html`<button
            @click=${() => {
                this.openDeleteRowConfirmation(rowNumber);
                this.setColumns();
            }}
            class="inline-delete-button ${this.compact ? "compact" : ""}"
        >
            ${remove}
        </button>`;
    }

    actionButtonsTemplate(
        row: FormattedRow,
        rowNumber: number,
        isEditing: boolean,
        isDeleting: boolean
    ) {
        if (!this.needActionColumn()) {
            return html``;
        }

        // To use dynamic tags, lit has to use staticHtml, these can't be defined
        // through the plain html literal tag. Pass along some information to the
        // buttons, so that they might use it how they see fit.
        return html`<td>
            <div class="action-buttons ${this.compact ? "compact" : ""}">
                <span>
                    ${this.inlineEditButtonTemplate(
                        row,
                        rowNumber,
                        isEditing,
                        isDeleting
                    )}
                    ${this.actionButtons.map((componentName) => {
                        const tag = `c-${componentName}`;
                        const extraProperties = {};
                        this.actionButtonsProperties.forEach((property) => {
                            const extraPropertyValue = lodashGet(
                                row.ref,
                                property
                            );
                            if (extraPropertyValue !== undefined) {
                                extraProperties[property] = extraPropertyValue;
                            }
                        });

                        return staticHtml`<${unsafeStatic(tag)}
              data-id=${row.ref.id}
              data-extra-properties=${JSON.stringify(extraProperties)}
              data-service-repository=${this.repositoryName}
              data-compact=${this.compact}>
            </${unsafeStatic(tag)}>`;
                    })}
                    ${this.generatedActionButtonsTemplate(
                        row,
                        rowNumber,
                        isEditing,
                        isDeleting
                    )}
                    ${this.inlineDeleteButtonTemplate(
                        row,
                        rowNumber,
                        isEditing,
                        isDeleting
                    )}
                </span>
            </div>
        </td>`;
    }

    generatedActionButtonsTemplate(
        row: FormattedRow,
        rowNumber: number,
        isEditing: boolean,
        isDeleting: boolean
    ) {
        if (!this.shouldGenerateActions()) {
            return html``;
        }

        if (isEditing || isDeleting) {
            return html``;
        }

        const actions = this.actionsManager.getActionsForItem(row.ref);
        return actions.map((action) => {
            // When a custom component is defined for the action, the component
            // gets full control over what should happen when the action button is
            // pressed.
            if (action.name in this.componentForActions) {
                const buttonTag = this.componentForActions[action.name];

                // When an empty tag is passed for a specific action, don't render
                // the button at all. This allows users of the table to hide certain
                // actions.
                if (buttonTag === "") {
                    return html``;
                }

                return staticHtml`<${unsafeStatic(buttonTag)}
          class="inline-action-button"
          data-service-repository=${action.repository.name}
          data-service-repository-action-name=${action.repositoryAction}
          data-title=${action.title}
          data-parameters=${JSON.stringify(action.parameters)}
        >
          ${action.title}
        </${unsafeStatic(buttonTag)}>`;
            } else {
                return html`<button
                    class="inline-action-button ${action.name}"
                    @click=${() => {
                        action.repository.objects[action.repositoryAction](
                            action.parameters
                        );
                    }}
                >
                    ${action.title}
                </button>`;
            }
        });
    }

    inlineCreateActionButtonsTemplate() {
        return html`
            <div class="action-buttons ${this.compact ? "compact" : ""}">
                <span>
                    <c-form-button
                        data-compact=${this.compact}
                        data-proxy-action="submit"
                        @click="${() => this.save("create")}"
                        data-inline
                    ></c-form-button
                    ><button
                        class="inline-cancel-button ${this.compact
                            ? "compact"
                            : ""}"
                        @click=${() => {
                            this.stopCreating();
                            this.setColumns();
                        }}
                    >
                        ${smallCancel}
                    </button>
                </span>
            </div>
        `;
    }

    shouldGenerateActions() {
        return (
            this.hasItemActionsAccordingToRepository &&
            !this.avoidGeneratingActions
        );
    }

    needActionColumn() {
        const shouldShowCustomActionButtons = this.actionButtons.length > 0;

        return (
            shouldShowCustomActionButtons ||
            this.inlineCreate ||
            this.inlineEdit ||
            this.inlineDelete ||
            this.shouldGenerateActions()
        );
    }

    bodyTemplate(rows: FormattedRows) {
        if (this.loading) {
            return html`<tbody>
                <tr>
                    <td colspan=${this.colspan} class="loading">Loading..</td>
                </tr>
            </tbody>`;
        }

        if (rows.length === 0) {
            return html`<tbody>
                ${!this.inlineCreate
                    ? html`<tr>
                          <td colspan=${this.colspan} class="no-results">
                              No results found.
                          </td>
                      </tr>`
                    : this.inlineCreateTemplate()}
            </tbody>`;
        }

        return html`<tbody>
            ${map(rows, (rowObj, rowNumber) => {
                const isEditing = rowNumber === this.rowNumberBeingEdited;
                const isDeleting = rowNumber === this.rowNumberBeingDeleted;
                return html`<tr
                        part="row"
                        @click=${(e) => {
                            // No need to emit the event when the row is expanded. In this
                            // case the interaction should be handled by the expandable row.
                            if (this.expandedRow === rowNumber) {
                                return;
                            }
                            this.dispatchEvent(
                                new CustomEvent("row-clicked", {
                                    detail: {
                                        row: rowObj.ref,
                                        event: e,
                                    },
                                    bubbles: true,
                                    composed: true,
                                })
                            );
                        }}
                    >
                        ${this.componentOnExpand
                            ? html`<td class="expanded-icon">
                                  <button
                                      class="expand-button"
                                      @click=${() => {
                                          this.setExpandedRow(rowNumber);
                                      }}
                                  >
                                      ${this.expandedRow === rowNumber
                                          ? chevronDown
                                          : chevronRight}
                                  </button>
                              </td>`
                            : ``}
                        ${this.cellsTemplate(rowObj, isEditing)}
                        ${this.actionButtonsTemplate(
                            rowObj,
                            rowNumber,
                            isEditing,
                            isDeleting
                        )}
                    </tr>
                    ${this.expandedRowTemplate(rowObj, rowNumber)} `;
            })}
            ${this.inlineCreateTemplate()}
        </tbody>`;
    }

    setExpandedRow(rowNumber) {
        if (this.expandedRow === rowNumber) {
            this.expandedRow = undefined;
        } else {
            this.expandedRow = rowNumber;
        }
    }

    expandedRowTemplate(row, rowNumber) {
        if (!this.componentOnExpand) {
            return;
        }
        if (this.expandedRow !== rowNumber) {
            return;
        }
        const tag = `c-${this.componentOnExpand}`;
        return staticHtml`<tr>
    <td colspan=${this.colspan} class="expanded-row">
    <${unsafeStatic(tag)} data-data=${JSON.stringify(row.ref)}></${unsafeStatic(
        tag
    )}>
    </td>
    </tr>`;
    }

    inlineCreateTemplate() {
        if (!this.inlineCreate) {
            return html``;
        }

        if (!this.isCreating) {
            return html`<tr>
                <td colspan=${this.colspan} class="create-new-entry">
                    <div
                        class="add-row-wrapper ${this.compact ? "compact" : ""}"
                        part="add-row-wrapper"
                    >
                        <button
                            class="add-row ${this.compact ? "compact" : ""}"
                            @click=${() => {
                                this.stopEditing();
                                this.isCreating = true;
                                this.setColumns();
                            }}
                        >
                            ${plus} Add
                        </button>
                    </div>
                </td>
            </tr>`;
        }

        const repository = serviceRepository.get(this.repositoryName);

        return html`<tr>
            ${this.componentOnExpand ? html`<td></td>` : ``}
            ${this.columnNames.map((columnName) => {
                // fieldName.properties.nestedFieldName instead of fieldName.nestedFieldName.
                let fieldLocator = columnName;
                let propertiesLocator = fieldLocator
                    .split(".")
                    .join(".properties.");

                if (this.mode === wrappedMode) {
                    const name = this.name.split(".").join(".properties.");
                    // When nested, we should navigate to properties using
                    // fieldName.properties.nestedFieldName instead of fieldName.nestedFieldName.
                    propertiesLocator = `${name}.properties.${propertiesLocator}`;
                    fieldLocator = `${this.name}.${fieldLocator}`;
                }

                // When the field does not exist in the 'create' properties, it means
                // that the field is not modifyable. Thus, instead of showing a form-input
                // we should just show the normal table cell instead.
                const existsInCreate = lodashHas(
                    repository.properties.create,
                    propertiesLocator
                );

                // Since we don't know what the value is going to be, just show an
                // empty cell in case it is not a field that we can submit in the
                // create.
                if (!existsInCreate) {
                    return html`<td></td>`;
                }

                return html`<td>
                    <c-form-input
                        data-name=${fieldLocator}
                        data-inline
                    ></c-form-input>
                </td>`;
            })}
            <td>${this.inlineCreateActionButtonsTemplate()}</td>
        </tr>`;
    }

    // When editing a row, not all cells might be editable. A table might show
    // a row which contains a value which is not updateable, e.g. a read-only
    // "created" field. This function checks if a cell should be editable or
    // not.
    shouldShowEditableCell(columnName: string) {
        // When the mode is 'wrapped', there are no multiple repository action
        // properties that we need to check for. Since we're managing our own data,
        // we can simply show all cells as editable.
        if (this.mode === wrappedMode) {
            return true;
        }

        const repository = serviceRepository.get(this.repositoryName);
        // When nested, we should navigate to properties using
        // fieldName.properties.nestedFieldName instead of fieldName.nestedFieldName.
        const fieldLocator = columnName.split(".").join(".properties.");
        // When the field does not exist in the 'update' properties, it means
        // that the field is not modifyable. Thus, instead of showing a form-input
        // we should just show the normal table cell instead.
        const existsInUpdate = lodashHas(
            repository.properties.update,
            fieldLocator
        );
        // When the field does not exist in the 'get' properties, it means that
        // we cannot prefill the form input. Instead, we will just show the
        // normal table cell.
        const existsInGet = lodashHas(repository.properties.get, fieldLocator);
        const existsInRowData = lodashHas(this.rowData, columnName);
        return existsInUpdate && existsInGet && existsInRowData;
    }

    cellsTemplate(rowObject: FormattedRow, isEditing: boolean) {
        if (isEditing) {
            if (!this.rowData) {
                return html`<td colspan=${this.colspan} class="loading">
                    loading...
                </td>`;
            }

            return this.columnNames.map((columnName, i) => {
                if (!this.shouldShowEditableCell(columnName)) {
                    return html`<td>${rowObject.row[i]}</td>`;
                }

                let value = lodashGet(this.rowData, columnName);
                let fieldLocator = columnName;
                if (this.mode === wrappedMode) {
                    fieldLocator = `${this.name}.${fieldLocator}`;

                    // In the case of the 'wrapped' mode, data can exist in several
                    // forms. It could be that the table is provided with data like:
                    // { user: { id: 1 }, where the fieldname of the form-input is
                    // user_id. In that case, try to retrieve the data through the
                    // alternate user.id locator instead of the user_id locator.
                    //
                    // Note that we can't make this assumption always, because the data
                    // might also only exist in the form of { user_id: 1 }, which happens
                    // after creating or editing a row.
                    const fieldExistsInData = lodashHas(
                        this.rowData,
                        columnName
                    );
                    if (!fieldExistsInData && columnName.endsWith("_id")) {
                        const alternateLocator = columnName.replace(
                            "_id",
                            ".id"
                        );
                        value = lodashGet(this.rowData, alternateLocator);
                    }
                }
                return html`<td>
                    <c-form-input
                        data-name=${fieldLocator}
                        data-inline
                        data-value=${value}
                    ></c-form-input>
                </td>`;
            });
        }

        return map(rowObject.row, (valueOrObject, index) => {
            const columnFieldType = this.converterPerColumn[index];
            const convertedValue = this.mappedValueConverter[columnFieldType](
                valueOrObject,
                rowObject.ref,
                this.extraPropertiesPerColumn[index]
            );
            return this.templatePerColumn[index](convertedValue);
        });
    }

    defaultTemplate(value) {
        return html` <td>${value}</td> `;
    }

    ellipsisTemplate(value) {
        return html`
            <td
                class="ellipsis"
                @mouseover=${(event) => {
                    const { target } = event;
                    if (target.scrollWidth > target.clientWidth) {
                        target.title = target.innerText;
                    }
                }}
                @mouseleave=${(event) => {
                    const { target } = event;
                    target.removeAttribute("title");
                }}
            >
                ${value}
            </td>
        `;
    }

    truncatedTextTemplate(value: string | object) {
        if (typeof value === "object") {
            return html`
                <td>
                    <span title=${value?.fullValue}
                        >${value?.truncatedText}</span
                    >
                </td>
            `;
        }
        return html` <td>${value}</td> `;
    }

    rightAlignmentTemplate(value) {
        return html` <td style="text-align: right">${value}</td> `;
    }

    imagePreviewTemplate(value) {
        return html`
            <td>
                <div class="cell-image-preview">
                    <img src=${value} alt="image-preview" />
                </div>
            </td>
        `;
    }

    badgeTemplate(value) {
        return html`
            <td>
                <span
                    class=${"ml-10 badge " +
                    value.toLowerCase().replaceAll(" ", "-")}
                    >${value}</span
                >
            </td>
        `;
    }

    valueSuffixTemplate(objectValue: IObjectValue) {
        if (objectValue.value && "valueSuffix" in objectValue) {
            return html`<td>
                ${objectValue.value}
                <span class="display-inline">${objectValue.valueSuffix}</span>
            </td> `;
        }
        return html`<td>${objectValue.value}</td>`;
    }

    inlineBadgeTemplate(objectValue: IObjectValue) {
        if ("badge" in objectValue) {
            return html`<td>
                ${objectValue.value}
                <span class="badge display-inline ${objectValue.badge}"
                    >${objectValue.badge}</span
                >
            </td> `;
        }
        return html`<td>${objectValue.value}</td>`;
    }

    filterTemplate() {
        const filterClasses = {
            "table-filter": !this.isAvoidSearch,
            "compact": this.compact,
            "flex-table": this.tableName,
        };

        return html`<div class=${classMap(filterClasses)}>
            <div class="filter-wrapper">
                ${this.tableName
                    ? html`<h2 class="m-0">${this.tableName}</h2>`
                    : ""}
                <slot name="left-filter"></slot>
            </div>

            <div class="filter-wrapper">
                <slot name="right-filter"></slot>
                ${!this.isAvoidSearch ? this.searchTemplate() : nothing}
            </div>
        </div>`;
    }

    searchTemplate() {
        if (
            this.isPaginatedThroughRepository &&
            !this.isSearchableThroughRepository
        ) {
            return nothing;
        }

        return html`
      <input placeholder="Search" @input=${({
          target,
      }: {
          target: HTMLInputElement;
      }) => this.throttledSearch(target.value)} .value=${
          this.searchFor
      }></input></div>`;
    }

    paginatorTemplate() {
        if (!this.isPaginatedThroughRepository) {
            return this.fakePaginatorTemplate();
        }

        const start = this.getStartRowNr() + 1;
        let end = start + this.rowsPerPage - 1;
        if (end > this.total) {
            end = this.total;
        }

        return html`<div class="paginator ${this.compact ? "compact" : ""}">
            <span>Showing ${start} to ${end} of ${this.total}</span>
            ${this.paginatorButtonsTemplate()}
        </div>`;
    }

    // When the repository is not paginated, fake pagination when the number
    // of rows exceeds "rowsPerPage".
    fakePaginatorTemplate() {
        const unpaginatedRows = this.getSortedAndFilteredRows();
        const start = this.getStartRowNr() + 1;
        const end = this.getEndRowNr(unpaginatedRows, start) - 1;
        return html`<div class="paginator ${this.compact ? "compact" : ""}">
            <span>Showing ${start} to ${end} of ${unpaginatedRows.length}</span
            >${this.paginatorButtonsTemplate(unpaginatedRows)}
        </div>`;
    }

    updateCurrentPage(page) {
        const prevPage = this.currentPage;
        this.currentPage = page;

        route.updateURLSearchParams({ page: `${this.currentPage}` });

        if (this.isPaginatedThroughRepository && prevPage !== page) {
            this.retrieveAndStoreTableData();
        }
    }

    paginatorButtonsTemplate(unpaginatedRows?: FormattedRows) {
        const pages = this.isPaginatedThroughRepository
            ? this.totalPages
            : Math.ceil(unpaginatedRows.length / this.rowsPerPage);
        const totalButtonsToShow = Math.min(this.maxPageButtons, pages);
        let startCountingFrom = 1;
        // At most we're showing 5 page buttons, once we are e.g. on page 4,
        // we want to show the buttons for page 2, 3, 4, 5 and 6. By checking
        // if we're over 3 pages, we can show the current page as the most middle
        // button of the 5 buttons.
        if (this.currentPage > 3) {
            // Always take the smallest so that we don't show buttons for pages
            // that don't exist. Current page minus 2 means that on page 4, we start
            // counting from 2. However, if we e.g. can show 4 pages at most, make
            // sure that we don't start counting from page 2. This is calculated
            // by taking the max pages and subtracting the total buttons that we can
            // show.
            startCountingFrom = Math.min(
                this.currentPage - 2,
                pages - totalButtonsToShow + 1
            );
        }
        return html`
            <button
                @click=${() => {
                    this.stopEditing();
                    this.updateCurrentPage(1);
                }}
            >
                ${chevronDoubleLeft}
            </button>
            <button
                @click=${() => {
                    this.stopEditing();
                    if (this.currentPage > 1) {
                        this.updateCurrentPage(this.currentPage - 1);
                    }
                }}
            >
                ${chevronLeft}
            </button>
            ${[...Array(totalButtonsToShow).keys()].map(
                (_, i) =>
                    html`<button
                        class=${this.currentPage === startCountingFrom + i
                            ? "current-page"
                            : nothing}
                        @click=${() => {
                            this.stopEditing();
                            this.updateCurrentPage(startCountingFrom + i);
                        }}
                    >
                        ${startCountingFrom + i}
                    </button>`
            )}
            <button
                @click=${() => {
                    this.stopEditing();
                    if (this.currentPage < pages) {
                        this.updateCurrentPage(this.currentPage + 1);
                    }
                }}
            >
                ${chevronRight}
            </button>
            <button
                @click=${() => {
                    this.stopEditing();
                    this.updateCurrentPage(pages);
                }}
            >
                ${chevronDoubleRight}
            </button>
        `;
    }

    scrollableTableTemplate(rows) {
        return html`
            <div class="table-scroll-wrapper">
                <table class="${this.compact ? "compact" : nothing}">
                    ${this.headersTemplate()} ${this.bodyTemplate(rows)}
                </table>
            </div>
        `;
    }

    baseTableTemplate(rows) {
        return html`
            <table class="${this.compact ? "compact" : nothing}">
                ${this.headersTemplate()} ${this.bodyTemplate(rows)}
            </table>
        `;
    }

    tableTemplate(rows) {
        const shouldShowPaginator = this.shouldShowPaginator(rows);

        // When we're faking pagination, make sure to only show a subset of the
        // total rows, specific to the page that we are on.
        if (!this.isPaginatedThroughRepository && shouldShowPaginator) {
            rows = this.getFakePaginatedRows(rows);
        }

        return html`
            ${this.badgeStyleTemplate()} ${this.filterTemplate()}
            ${this.scrollable
                ? this.scrollableTableTemplate(rows)
                : this.baseTableTemplate(rows)}
            ${shouldShowPaginator ? this.paginatorTemplate() : nothing}
        `;
    }

    badgeStyleTemplate() {
        if (this.badges.length === 0) {
            return html``;
        }
        const availableBadges = this.badges.filter(
            (badge) => this.tableBadgeObject[badge]
        );
        return html`<style>
            ${availableBadges.map((badge) => {
                const badgeStyles = Object.entries(this.tableBadgeObject[badge])
                    .map(
                        ([cssProperty, cssValue]) =>
                            `${cssProperty}: ${cssValue};`
                    )
                    .join("");

                return `.badge.${badge} { ${badgeStyles}}`;
            })}
        </style>`;
    }

    render() {
        let rows = [];
        if (!this.loading) {
            rows = this.getSortedAndFilteredRows();
        }

        // The c-form even exists when nothing is currently being created, edited
        // or deleted. This avoids a rerender when switching to actually editing a
        // row.
        const wrapInForm =
            this.mode === standaloneMode &&
            (this.inlineCreate || this.inlineEdit || this.inlineDelete);
        if (wrapInForm) {
            const action = this.isCreating ? "create" : "update";
            return html`
                <c-form
                    data-service-repository=${this.repositoryName}
                    data-action=${action}
                    data-avoid-submit-button
                    data-extra-form-data=${JSON.stringify(
                        this.serviceRepositoryFields
                    )}
                    ?data-avoid-toast-on-submit=${this.avoidToastOnSubmit}
                    data-skip-prefill
                >
                    ${this.tableTemplate(rows)}
                </c-form>
            `;
        }

        return html`${this.tableTemplate(rows)} `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "c-table": TableComponent;
    }
}
